import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Box,
    Flex,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    useColorModeValue,
    IconButton,
} from '@chakra-ui/react';
import { 
    MdOutlineKeyboardArrowLeft, 
    MdOutlineKeyboardArrowRight,
} from 'react-icons/md';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { FilterHarvestMap } from '../components/FilterHarvestMap';
import { MapControls } from '../components/MapControls';
import { MapMarkerDetailModal } from '../components/MapMarkerDetailModal';
import { HarvestMapService } from '../services/harvestMapService';
import type { HarvestForemanRecordWithCoords, TphDetailsWithCoords, HarvestMonitoringFilters } from '../types';
import Navbar from '../../../components/Navbar';
import { getDistance } from '../utils/utils';


export const HarvestMapPage: React.FC = () => {
    const toast = useToast();
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const [map, setMap] = useState<maplibregl.Map | null>(null);
    const [isSatellite, setIsSatellite] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [records, setRecords] = useState<HarvestForemanRecordWithCoords[]>([]);
    const [activeFilters, setActiveFilters] = useState<HarvestMonitoringFilters | null>(null);
    const [selectedTph, setSelectedTph] = useState<TphDetailsWithCoords | null>(null);
    const [selectedForemanGroupName, setSelectedForemanGroupName] = useState<string | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
    const [activeDetailModalType, setActiveDetailModalType] = useState<'tph' | 'harvest' | null>(null);

    // Keep a ref of records to prevent stale closures in map click listener
    const recordsRef = useRef<HarvestForemanRecordWithCoords[]>([]);
    recordsRef.current = records;

    // Ref to hold current coordinates boundary to support focus to area button
    const coordinatesToFitRef = useRef<[number, number][]>([]);

    // Color mode values
    const sidebarBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const hoverBg = useColorModeValue('gray.50', 'gray.700');

    // Initialize Map
    useEffect(() => {
        if (!mapContainerRef.current) return;

        const mapInstance = new maplibregl.Map({
            container: mapContainerRef.current,
            style: 'https://api.maptiler.com/maps/streets-v2/style.json?key=Q3fXi2sghTMg1wQSFgxK',
            center: [104.083064, -3.125992],
            zoom: 11,
            attributionControl: false,
        });

        mapInstance.on('load', async () => {
            // Load GeoJSON Block boundaries
            try {
                const geojsonResponse = await fetch('/data/batas_blok_kebun_gbs.geojson');
                if (geojsonResponse.ok) {
                    const geojsonData = await geojsonResponse.json();
                    
                    // Add default color to blocks
                    geojsonData.features.forEach((feature: any) => {
                        feature.properties.color = 'rgba(255, 255, 255, 0.1)';
                    });

                    mapInstance.addSource('geojson-source', {
                        type: 'geojson',
                        data: geojsonData,
                    });

                    mapInstance.addLayer({
                        id: 'geojson-layer',
                        type: 'fill',
                        source: 'geojson-source',
                        paint: {
                            'fill-color': ['get', 'color'],
                            'fill-opacity': 0.4,
                            'fill-outline-color': '#000000',
                        },
                    });

                    mapInstance.addLayer({
                        id: 'geojson-label',
                        type: 'symbol',
                        source: 'geojson-source',
                        layout: {
                            'text-field': ['get', 'LandUse_26'],
                            'text-size': 10,
                            'text-anchor': 'center',
                            'text-allow-overlap': false,
                        },
                        paint: {
                            'text-color': '#000000',
                            'text-halo-color': '#ffffff',
                            'text-halo-width': 1,
                        },
                    });
                }

                // Load LKK boundaries
                const lkkResponse = await fetch('/data/Blok_Planted_LKK.geojson');
                if (lkkResponse.ok) {
                    const lkkData = await lkkResponse.json();
                    
                    lkkData.features.forEach((feature: any) => {
                        feature.properties.color = 'rgba(255, 255, 255, 0.1)';
                    });

                    mapInstance.addSource('lkk-source', {
                        type: 'geojson',
                        data: lkkData,
                    });

                    mapInstance.addLayer({
                        id: 'lkk-layer',
                        type: 'fill',
                        source: 'lkk-source',
                        paint: {
                            'fill-color': ['get', 'color'],
                            'fill-opacity': 0.4,
                            'fill-outline-color': '#000000',
                        },
                    });

                    mapInstance.addLayer({
                        id: 'lkk-label',
                        type: 'symbol',
                        source: 'lkk-source',
                        layout: {
                            'text-field': ['get', 'blok_code'],
                            'text-size': 10,
                            'text-anchor': 'center',
                            'text-allow-overlap': false,
                        },
                        paint: {
                            'text-color': '#000000',
                            'text-halo-color': '#ffffff',
                            'text-halo-width': 1,
                        },
                    });
                }

                // Load MRN boundaries
                const mrnResponse = await fetch('/data/mrn_map.geojson');
                if (mrnResponse.ok) {
                    const mrnData = await mrnResponse.json();
                    
                    mrnData.features.forEach((feature: any) => {
                        feature.properties.color = 'rgba(255, 255, 255, 0.1)';
                    });

                    mapInstance.addSource('mrn-source', {
                        type: 'geojson',
                        data: mrnData,
                    });

                    mapInstance.addLayer({
                        id: 'mrn-layer',
                        type: 'fill',
                        source: 'mrn-source',
                        paint: {
                            'fill-color': ['get', 'color'],
                            'fill-opacity': 0.4,
                            'fill-outline-color': '#000000',
                        },
                    });

                    mapInstance.addLayer({
                        id: 'mrn-label',
                        type: 'symbol',
                        source: 'mrn-source',
                        layout: {
                            'text-field': ['coalesce', ['get', 'blok_code'], ['get', 'LandUse_26'], ''],
                            'text-size': 10,
                            'text-anchor': 'center',
                            'text-allow-overlap': false,
                        },
                        paint: {
                            'text-color': '#000000',
                            'text-halo-color': '#ffffff',
                            'text-halo-width': 1,
                        },
                    });
                }
            } catch (err) {
                console.error('Error loading GeoJSON boundaries:', err);
            }

            // Add satellite source
            mapInstance.addSource('satellite', {
                type: 'raster',
                tiles: [
                    'https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=QY0dAspaeAOotq0bMeLo',
                ],
                tileSize: 256,
            });

            mapInstance.addLayer({
                id: 'satellite-layer',
                type: 'raster',
                source: 'satellite',
                layout: { visibility: 'none' },
            });

            // Move satellite layer to bottom so vectors stay visible on top
            if (mapInstance.getLayer('geojson-layer')) {
                mapInstance.moveLayer('satellite-layer', 'geojson-layer');
            } else if (mapInstance.getLayer('lkk-layer')) {
                mapInstance.moveLayer('satellite-layer', 'lkk-layer');
            } else if (mapInstance.getLayer('mrn-layer')) {
                mapInstance.moveLayer('satellite-layer', 'mrn-layer');
            }
        });

        // Add Click & Hover Interaction for WebGL markers
        mapInstance.on('click', 'harvest-markers', (e) => {
            const features = mapInstance.queryRenderedFeatures(e.point, { layers: ['harvest-markers'] });
            if (!features.length) return;
            const feature = features[0];
            const props = feature.properties as any;
            
            // Find the full Tph object from records ref
            let foundTph: TphDetailsWithCoords | null = null;
            let foundForemanGroupName = '';
            for (const rkh of recordsRef.current) {
                for (const foreman of rkh.foremen) {
                    for (const tph of foreman.tph_details) {
                        if (tph.tph_code === props.tph_code && tph.bcc_number === props.bcc_number) {
                            foundTph = tph;
                            foundForemanGroupName = foreman.foreman_group_name;
                            break;
                        }
                    }
                    if (foundTph) break;
                }
                if (foundTph) break;
            }

            if (foundTph) {
                setSelectedTph(foundTph);
                setSelectedForemanGroupName(foundForemanGroupName);
                if (props.marker_type === 'tph') {
                    setActiveDetailModalType('tph');
                } else if (props.marker_type === 'harvest') {
                    setActiveDetailModalType('harvest');
                }
            }
        });

        mapInstance.on('mouseenter', 'harvest-markers', () => {
            mapInstance.getCanvas().style.cursor = 'pointer';
        });
        mapInstance.on('mouseleave', 'harvest-markers', () => {
            mapInstance.getCanvas().style.cursor = '';
        });

        setMap(mapInstance);

        return () => {
            mapInstance.remove();
        };
    }, []);

    // Toggle Satellite View
    const toggleSatellite = () => {
        if (!map) return;
        const newSatelliteState = !isSatellite;
        setIsSatellite(newSatelliteState);
        map.setLayoutProperty(
            'satellite-layer',
            'visibility',
            newSatelliteState ? 'visible' : 'none'
        );
    };

    // Clean existing markers and lines
    const clearMapObjects = useCallback(() => {
        if (map) {
            if (map.getLayer('harvest-lines')) {
                map.removeLayer('harvest-lines');
            }
            if (map.getLayer('harvest-markers-labels')) {
                map.removeLayer('harvest-markers-labels');
            }
            if (map.getLayer('harvest-markers')) {
                map.removeLayer('harvest-markers');
            }
            if (map.getSource('harvest-lines-source')) {
                map.removeSource('harvest-lines-source');
            }
            if (map.getSource('harvest-markers-source')) {
                map.removeSource('harvest-markers-source');
            }
        }
    }, [map]);

    // Plot data on map
    const plotData = useCallback((data: HarvestForemanRecordWithCoords[]) => {
        if (!map) return;

        clearMapObjects();
        setSelectedTph(null);
        setSelectedForemanGroupName(null);

        const linesFeatures: any[] = [];
        const markerFeatures: any[] = [];
        const coordinatesToFit: [number, number][] = [];

        data.forEach((record) => {
            record.foremen.forEach((foreman) => {
                foreman.tph_details.forEach((tph) => {
                    const hasHarvestCoords = tph.latitude && tph.longitude && tph.latitude !== 0;
                    const hasTphCoords = tph.tph_latitude && tph.tph_longitude && tph.tph_latitude !== 0;

                    if (hasHarvestCoords && hasTphCoords) {
                        const harvestLon = tph.longitude!;
                        const harvestLat = tph.latitude!;
                        const tphLon = tph.tph_longitude!;
                        const tphLat = tph.tph_latitude!;

                        // Calculate distance
                        const distance = getDistance(harvestLat, harvestLon, tphLat, tphLon);
                        const isFar = distance > 50; // Red if > 50 meters, green/orange if close

                        coordinatesToFit.push([harvestLon, harvestLat]);
                        coordinatesToFit.push([tphLon, tphLat]);

                        // Shared properties for popup detail queries
                        const sharedProps = {
                            tph_code: tph.tph_code,
                            bcc_number: tph.bcc_number,
                            harvester_name: tph.harvester_name,
                            foreman_name: tph.foreman_name,
                            bunch_qty: tph.bunch_qty,
                            distance: distance,
                            created_by: tph.created_by
                        };

                        // 1. Point Feature for Harvest (User input location) - Red/Orange
                        markerFeatures.push({
                            type: 'Feature',
                            properties: {
                                ...sharedProps,
                                marker_type: 'harvest',
                                color: isFar ? '#E53E3E' : '#DD6B20', // Red or Orange
                            },
                            geometry: {
                                type: 'Point',
                                coordinates: [harvestLon, harvestLat],
                            },
                        });

                        // 2. Point Feature for TPH Coordinates - Green
                        markerFeatures.push({
                            type: 'Feature',
                            properties: {
                                ...sharedProps,
                                marker_type: 'tph',
                                color: '#38A169', // Green
                            },
                            geometry: {
                                type: 'Point',
                                coordinates: [tphLon, tphLat],
                            },
                        });

                        // 3. Connecting Line Feature
                        linesFeatures.push({
                            type: 'Feature',
                            properties: {
                                color: isFar ? '#E53E3E' : '#38A169', // Red for far, green for safe
                                width: isFar ? 3 : 1.5,
                            },
                            geometry: {
                                type: 'LineString',
                                coordinates: [
                                    [harvestLon, harvestLat],
                                    [tphLon, tphLat],
                                ],
                            },
                        });
                    }
                });
            });
        });

        // Add Markers source & circle layer to map (WebGL rendering)
        if (markerFeatures.length > 0) {
            map.addSource('harvest-markers-source', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: markerFeatures,
                },
            });

            map.addLayer({
                id: 'harvest-markers',
                type: 'circle',
                source: 'harvest-markers-source',
                paint: {
                    'circle-radius': 6.5,
                    'circle-color': ['get', 'color'],
                    'circle-stroke-width': 1.5,
                    'circle-stroke-color': '#FFFFFF',
                    'circle-opacity': 0.9,
                },
            });

            // Add Text labels for green TPH markers
            map.addLayer({
                id: 'harvest-markers-labels',
                type: 'symbol',
                source: 'harvest-markers-source',
                filter: ['==', ['get', 'marker_type'], 'tph'],
                layout: {
                    'text-field': ['get', 'tph_code'],
                    'text-size': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        10, 6,
                        14, 10,
                        18, 14
                    ],
                    'text-offset': [0, 1.2],
                    'text-anchor': 'top',
                    'text-allow-overlap': false,
                    'text-ignore-placement': false,
                    'text-optional': true,
                },
                paint: {
                    'text-color': '#1C5B37',
                    'text-halo-color': '#FFFFFF',
                    'text-halo-width': 1.5,
                    'text-opacity': [
                        'step',
                        ['zoom'],
                        0,
                        14, 1
                    ]
                },
            });
        }

        // Add line source & layer to map
        if (linesFeatures.length > 0) {
            map.addSource('harvest-lines-source', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: linesFeatures,
                },
            });

            map.addLayer({
                id: 'harvest-lines',
                type: 'line',
                source: 'harvest-lines-source',
                paint: {
                    'line-color': ['get', 'color'],
                    'line-width': ['get', 'width'],
                    'line-opacity': 0.8,
                },
            });
        }

        // Fit map bounds to show all markers
        coordinatesToFitRef.current = coordinatesToFit;
        if (coordinatesToFit.length > 0) {
            const bounds = coordinatesToFit.reduce(
                (acc, coord) => acc.extend(coord),
                new maplibregl.LngLatBounds(coordinatesToFit[0], coordinatesToFit[0])
            );
            map.fitBounds(bounds, { padding: 50, maxZoom: 16 });
        }
    }, [map, clearMapObjects]);

    // Handle search query
    const handleFiltersChange = async (filters: HarvestMonitoringFilters) => {
        setIsLoading(true);
        setActiveFilters(filters);
        try {
            const res = await HarvestMapService.getHarvestMapData(filters);
            if (res.status === 'success' && res.data) {
                setRecords(res.data);
                plotData(res.data);
            } else {
                setRecords([]);
                clearMapObjects();
            }
        } catch (error) {
            console.error('Error fetching harvest map data:', error);
            toast({
                title: 'Gagal mengambil data peta',
                description: 'Koneksi ke server terganggu.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
            setRecords([]);
            clearMapObjects();
        } finally {
            setIsLoading(false);
        }
    };

    // Zoom to specific foreman details group
    const focusOnForeman = (tphDetails: TphDetailsWithCoords[]) => {
        if (!map) return;
        const coords: [number, number][] = [];

        tphDetails.forEach((tph) => {
            if (tph.latitude && tph.longitude && tph.latitude !== 0) {
                coords.push([tph.longitude, tph.latitude]);
            }
            if (tph.tph_latitude && tph.tph_longitude && tph.tph_latitude !== 0) {
                coords.push([tph.tph_longitude, tph.tph_latitude]);
            }
        });

        if (coords.length > 0) {
            const bounds = coords.reduce(
                (acc, coord) => acc.extend(coord),
                new maplibregl.LngLatBounds(coords[0], coords[0])
            );
            map.fitBounds(bounds, { padding: 60, maxZoom: 16 });
        }
    };

    const focusToArea = () => {
        if (!map) return;
        if (coordinatesToFitRef.current.length > 0) {
            const bounds = coordinatesToFitRef.current.reduce(
                (acc, coord) => acc.extend(coord),
                new maplibregl.LngLatBounds(coordinatesToFitRef.current[0], coordinatesToFitRef.current[0])
            );
            map.fitBounds(bounds, { padding: 50, maxZoom: 16 });
        } else {
            // Default center if no coordinates loaded yet
            map.flyTo({ center: [104.083064, -3.125992], zoom: 11 });
        }
    };

    const zoomIn = () => {
        if (!map) return;
        map.zoomIn();
    };

    const zoomOut = () => {
        if (!map) return;
        map.zoomOut();
    };

    // Calculate totals of coordinates mapped vs unmapped
    let totalHarvestTrans = 0;
    let mappedHarvestTrans = 0;

    records.forEach((record) => {
        record.foremen.forEach((foreman) => {
            foreman.tph_details.forEach((t) => {
                totalHarvestTrans++;
                if (t.latitude && t.longitude && t.latitude !== 0 && t.tph_latitude && t.tph_longitude) {
                    mappedHarvestTrans++;
                }
            });
        });
    });

    return (
        <Box h="100vh" display="flex" flexDirection="column" overflow="hidden">
            <Navbar />
            
            {/* Split Screen Map Layout */}
            <Flex flex="1" overflow="hidden" position="relative" h="calc(100vh - 64px)">
                
                {/* Collapsible Left Sidebar */}
                <Box
                    position={{ base: 'fixed', md: 'fixed' }}
                    top="64px"
                    left={{ base: sidebarOpen ? 0 : '-100%', md: sidebarOpen ? 0 : -350 }}
                    width={{ base: 'calc(100% - 64px)', md: '350px' }}
                    height="calc(100vh - 64px)"
                    bg={sidebarBg}
                    boxShadow="lg"
                    zIndex={15}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    display="flex"
                    flexDirection="column"
                >
                    {/* Header/Title Inside Sidebar */}
                    <Box p={5}>
                        <Heading size="md" color="gray.800">
                            Peta Hasil Panen
                        </Heading>
                    </Box>

                    {/* Filter Section directly inside sidebar */}
                    <Box px={5} pb={5} bg="white">
                        <FilterHarvestMap onFiltersChange={handleFiltersChange} isLoading={isLoading} />
                    </Box>

                    {/* Scrollable Details Section inside sidebar */}
                    {/* <Box flex="1" overflowY="auto" p={4}>
                        {isLoading ? (
                            <Center h="100%">
                                <VStack spacing={3}>
                                    <Spinner size="lg" color="green.500" thickness="3px" />
                                    <Text fontSize="xs" color="gray.500">Memuat data...</Text>
                                </VStack>
                            </Center>
                        ) : records.length > 0 ? (
                            <VStack align="stretch" spacing={4}>
                                <Heading size="xx-small" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                                    Daftar Kemandoran
                                </Heading>
                                <Divider />

                                {records.map((rkh) => (
                                    <VStack key={rkh.rkh_nbr} align="stretch" spacing={3}>
                                        <Text fontSize="xs" fontWeight="bold" color="blue.500" bg="blue.50" px={2} py={1} borderRadius="md">
                                            No. RKH: {rkh.rkh_nbr}
                                        </Text>
                                        
                                        {rkh.foremen.map((foreman) => {
                                            const mappedInGroup = foreman.tph_details.filter(
                                                t => t.latitude && t.longitude && t.latitude !== 0 && t.tph_latitude && t.tph_longitude
                                            ).length;

                                            return (
                                                <Box
                                                    key={foreman.foreman_group_code}
                                                    p={3}
                                                    border="1px solid"
                                                    borderColor={borderColor}
                                                    borderRadius="lg"
                                                    bg={sidebarBg}
                                                    _hover={{ bg: hoverBg, cursor: 'pointer' }}
                                                    onClick={() => focusOnForeman(foreman.tph_details)}
                                                >
                                                    <VStack align="stretch" spacing={2}>
                                                        <Flex justify="space-between" align="start">
                                                            <Box maxW="70%">
                                                                <Text fontSize="xs" fontWeight="bold" noOfLines={1}>
                                                                    {foreman.foreman_group_name}
                                                                </Text>
                                                                <Text fontSize="xx-small" color="gray.400">
                                                                    Kode: {foreman.foreman_group_code}
                                                                </Text>
                                                            </Box>
                                                            <IconButton
                                                                aria-label="Focus Map"
                                                                icon={<MdMyLocation />}
                                                                size="xs"
                                                                colorScheme="green"
                                                                variant="ghost"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    focusOnForeman(foreman.tph_details);
                                                                }}
                                                            />
                                                        </Flex>

                                                        <Divider />

                                                        <Grid templateColumns="repeat(2, 1fr)" gap={2} fontSize="xx-small">
                                                            <Box>
                                                                <Text color="gray.400">BCC (Janjang)</Text>
                                                                <Text fontWeight="semibold">{foreman.bcc_bunch} jjg</Text>
                                                            </Box>
                                                            <Box>
                                                                <Text color="gray.400">SPB (Janjang)</Text>
                                                                <Text fontWeight="semibold">{foreman.spb_bunch} jjg</Text>
                                                            </Box>
                                                            <Box>
                                                                <Text color="gray.400">Pemanen (HK)</Text>
                                                                <Text fontWeight="semibold">{foreman.bcc_hk} HK</Text>
                                                            </Box>
                                                            <Box>
                                                                <Text color="gray.400">Terpetakan</Text>
                                                                <Badge fontSize="xx-small" colorScheme={mappedInGroup > 0 ? 'green' : 'gray'}>
                                                                    {mappedInGroup} TPH
                                                                </Badge>
                                                            </Box>
                                                        </Grid>
                                                    </VStack>
                                                </Box>
                                            );
                                        })}
                                    </VStack>
                                ))}
                            </VStack>
                        ) : (
                            <Center h="100%">
                                <Text fontSize="xs" color="gray.400" textAlign="center">
                                    Silakan tentukan filter kemudian klik "Cari Peta" untuk menampilkan rute panen kemandoran.
                                </Text>
                            </Center>
                        )}
                    </Box> */}

                </Box>
 
                {/* Sidebar Toggle Button */}
                <Box
                    position="fixed"
                    top="80px"
                    left={{
                        base: sidebarOpen ? 'calc(100% - 56px)' : '10px',
                        md: sidebarOpen ? '360px' : '10px'
                    }}
                    zIndex={20}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                >
                    <IconButton
                        aria-label={sidebarOpen ? "Sembunyikan Sidebar" : "Tampilkan Sidebar"}
                        icon={sidebarOpen ? <MdOutlineKeyboardArrowLeft /> : <MdOutlineKeyboardArrowRight />}
                        size="md"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        borderRadius="md"
                        boxShadow="md"
                        colorScheme="green"
                    />
                </Box>
 
                {/* Map Container - Dynamic margin based on sidebar state */}
                <Box 
                    flex="1" 
                    h="100%" 
                    position="relative" 
                    ref={mapContainerRef}
                    ml={{ base: 0, md: sidebarOpen ? '350px' : 0 }}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                >
                    {/* Map Controls */}
                    <MapControls
                        isSatellite={isSatellite}
                        onToggleSatellite={toggleSatellite}
                        onFocusToArea={focusToArea}
                        onZoomIn={zoomIn}
                        onZoomOut={zoomOut}
                    />

                    {/* Inline Legend Overlay */}
                    <Box
                        position="absolute"
                        bottom={{ base: "15%", sm: 6, md: 8 }}
                        left={{ base: 4, sm: 6, md: 8 }}
                        bg={useColorModeValue('white', 'gray.800')}
                        borderRadius="xl"
                        boxShadow="lg"
                        p={4}
                        zIndex={10}
                        border="1px solid"
                        borderColor={useColorModeValue('gray.100', 'gray.700')}
                        minW="180px"
                    >
                        <Text 
                            fontSize="xs" 
                            fontWeight="bold" 
                            color="black" 
                            mb={3}  
                            letterSpacing="wider"
                        >
                            Legend
                        </Text>
                        <VStack spacing={2.5} align="start">
                            <HStack spacing={2.5}>
                                <Box w="12px" h="12px" bg="#38A169" borderRadius="full" border="1.5px solid white" boxShadow="0 0 0 1px #CBD5E0" />
                                <Text fontSize="xs" fontWeight="medium" color={useColorModeValue('gray.700', 'gray.200')}>Titik TPH</Text>
                            </HStack>
                            <HStack spacing={2.5}>
                                <Box w="12px" h="12px" bg="#DD6B20" borderRadius="full" border="1.5px solid white" boxShadow="0 0 0 1px #CBD5E0" />
                                <Text fontSize="xs" fontWeight="medium" color={useColorModeValue('gray.700', 'gray.200')}>Lokasi Input BCC</Text>
                            </HStack>
                            <HStack spacing={2.5}>
                                <Box w="24px" h="3px" bg="#38A169" borderRadius="full" />
                                <Text fontSize="xs" fontWeight="medium" color={useColorModeValue('gray.700', 'gray.200')}>Jarak &lt; 50m</Text>
                            </HStack>
                            <HStack spacing={2.5}>
                                <Box w="24px" h="3px" bg="#E53E3E" borderRadius="full" />
                                <Text fontSize="xs" fontWeight="medium" color={useColorModeValue('gray.700', 'gray.200')}>Jarak &gt; 50m</Text>
                            </HStack>
                        </VStack>
                    </Box>
                </Box>
            </Flex>

            {/* Map Marker Detail Modal (TPH / Harvest) */}
            <MapMarkerDetailModal
                isOpen={activeDetailModalType !== null}
                onClose={() => {
                    setActiveDetailModalType(null);
                    setSelectedForemanGroupName(null);
                }}
                type={activeDetailModalType}
                tph={selectedTph}
                foremanGroupName={selectedForemanGroupName}
            />
        </Box>
    );
};
